// src/pages/onboarding/OnboardingLayout.tsx
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

export type OnboardingStepKey =
  | "bot"
  | "channels"
  | "booking"
  | "leadAds"
  | "plan"
  | "knowledge";

export interface OnboardingLayoutProps {
  currentStep: OnboardingStepKey;
  botId?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  layout?: "split" | "full";
  rightAction?: React.ReactNode;
}

interface OnboardingStep {
  key: OnboardingStepKey;
  label: string;
  description: string;
  path: (botId: string) => string;
}

function getSteps(
  t: TFunction<"translation">,
  botId?: string
): OnboardingStep[] {
  const safeBotId = botId || "__bot__";

  const mkBotDepPath =
    (suffix: string) =>
    (id: string): string =>
      `/onboarding/bots/${encodeURIComponent(id || safeBotId)}${suffix}`;

  return [
    {
      key: "bot",
      label: t("onboardingLayout.steps.bot", "1. Create bot"),
      description: t(
        "onboardingLayout.steps.botDescription",
        "Name your assistant and choose how customers will see it."
      ),
      path: () => "/onboarding/bots/new"
    },
    {
      key: "channels",
      label: t("onboardingLayout.steps.channels", "2. Channels"),
      description: t(
        "onboardingLayout.steps.channelsDescription",
        "Connect WhatsApp, Facebook, Instagram or your website."
      ),
      path: mkBotDepPath("/channels")
    },
    {
      key: "booking",
      label: t("onboardingLayout.steps.booking", "3. Bookings"),
      description: t(
        "onboardingLayout.steps.bookingDescription",
        "Let Coslo propose and book meetings directly from chat."
      ),
      path: mkBotDepPath("/booking")
    },
    {
      key: "leadAds",
      label: t("onboardingLayout.steps.leadAds", "4. Lead Ads"),
      description: t(
        "onboardingLayout.steps.leadAdsDescription",
        "Sync your Facebook Lead Ads and send WhatsApp messages automatically."
      ),
      path: mkBotDepPath("/lead-ads")
    },
    {
      key: "plan",
      label: t("onboardingLayout.steps.plan", "5. Plan & billing"),
      description: t(
        "onboardingLayout.steps.planDescription",
        "Review configuration, pick your plan and start using Coslo."
      ),
      path: mkBotDepPath("/plan")
    },
    {
      key: "knowledge",
      label: t("onboardingLayout.steps.knowledge", "6. Knowledge"),
      description: t(
        "onboardingLayout.steps.knowledgeDescription",
        "Let Coslo learn from your website so it can answer detailed questions."
      ),
      path: mkBotDepPath("/knowledge")
    }
  ];
}

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  currentStep,
  botId,
  title,
  subtitle,
  children,
  layout,
  rightAction
}) => {
  const { t } = useTranslation();
  const steps = getSteps(t, botId);

  const currentIndex = steps.findIndex((s) => s.key === currentStep);
  const totalSteps = steps.length;
  const isFull = (layout ?? "split") === "full";

  return (
    <section className="auth-landing">
      <div className="lp-container">
        <div className="auth-layout-onboarding">
          {/* TOP: eyebrow + horizontal stepper */}
          <header className="onboarding-header">
            <div className="onboarding-header-top">
              <p className="auth-eyebrow">
                {t(
                  "onboardingLayout.eyebrow",
                  "Guided onboarding · {current}/{total}"
                )
                  .replace("{current}", String(currentIndex + 1))
                  .replace("{total}", String(totalSteps))}
              </p>
            </div>

            <ol className="onboarding-steps">
              {steps.map((step, index) => {
                const isActive = step.key === currentStep;
                const isCompleted = index < currentIndex;
                const isFuture = index > currentIndex;

                const stepClassName = [
                  "onboarding-step",
                  isActive && "onboarding-step--active",
                  isCompleted && "onboarding-step--done",
                  isFuture && "onboarding-step--future"
                ]
                  .filter(Boolean)
                  .join(" ");

                const canClick = step.key === "bot" || !!botId;

                const content = (
                  <>
                    <div className="onboarding-step-index">
                      {isCompleted ? "✓" : index + 1}
                    </div>
                    <div className="onboarding-step-text">
                      <div className="onboarding-step-label">{step.label}</div>
                      <div className="onboarding-step-description">
                        {step.description}
                      </div>
                    </div>
                  </>
                );

                if (!canClick) {
                  return (
                    <li key={step.key} className={stepClassName}>
                      <div className="onboarding-step-inner onboarding-step-inner--disabled">
                        {content}
                      </div>
                    </li>
                  );
                }

                const to =
                  step.key === "bot"
                    ? step.path("__ignored__")
                    : step.path(botId as string);

                return (
                  <li key={step.key} className={stepClassName}>
                    <Link className="onboarding-step-inner" to={to}>
                      {content}
                    </Link>
                  </li>
                );
              })}
            </ol>
          </header>

          {/* MAIN: text + content */}
          <div
            className={
              isFull
                ? "onboarding-main onboarding-main--full"
                : "onboarding-main"
            }
          >
            <div
              className={
                isFull
                  ? "onboarding-main-copy onboarding-main-copy--full"
                  : "onboarding-main-copy"
              }
            >
              <h1 className="auth-title">{title}</h1>
              {subtitle && <p className="auth-subtitle">{subtitle}</p>}
              <div className="onboarding-title-row">
                {rightAction && (
                  <div className="onboarding-header-actions">
                    {rightAction}
                  </div>
                )}
              </div>
            </div>

            <div
              className={
                isFull
                  ? "auth-card onboarding-card onboarding-main-card onboarding-main-card--full"
                  : "auth-card onboarding-card onboarding-main-card"
              }
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OnboardingLayout;
